import { Button } from '@99designs/common/components/Button';
import { ButtonGroup, ButtonGroupItem } from '@99designs/common/components/ButtonGroup';
import { claimPhotographyBrief } from '__generated__/claimPhotographyBrief';
import { claimVideoBrief } from '__generated__/claimVideoBrief';
import { getPhotographyBrief_structuredBrief } from '__generated__/getPhotographyBrief';
import { getVideoBrief_structuredBrief } from '__generated__/getVideoBrief';
import { Industry } from '__generated__/Industry';
import { StructuredBriefErrorReason as ErrorReason } from '__generated__/types';
import {
    updatePhotographyBrief_updateStructuredBrief_errors as PhotographyValidationError,
    updatePhotographyBrief_updateStructuredBrief_fields_SmartShootPhotographyFields as PhotographyBriefFields,
} from '__generated__/updatePhotographyBrief';
import {
    updateVideoBrief_updateStructuredBrief_errors as VideoValidationError,
    updateVideoBrief_updateStructuredBrief_fields_SmartShootVideoFields as VideoBriefFields,
} from '__generated__/updateVideoBrief';
import { Viewer } from '__generated__/Viewer';
import { ApolloClient } from 'apollo-client';
import Bugsnag, { handlePromise } from 'common/utils/Bugsnag';
import { scrollToElement } from 'common/utils/scroll';
import { AuthProviderLoginFn } from 'projects/components/AuthContext';
import { FormErrors, ObservableForm } from 'projects/components/ObservableForm';
import BriefFooter from 'projects/components/SmartShoot/BriefFooter';
import BriefHero from 'projects/components/SmartShoot/BriefHero';
import PackageForm from 'projects/components/SmartShoot/PackageForm';
import { trackViewedBriefEvent } from 'projects/events';
import { history } from 'projects/routes/BrowserRouter';
import React from 'react';
import { MutationFetchResult } from 'react-apollo';
import SmartShootUSOnly from './SmartShootUSOnly';

interface Props<B, F, V> {
    client: ApolloClient<any>;
    brief: B;
    viewer: Viewer;
    login: AuthProviderLoginFn;
    industries: Industry[];
    documentTitle: string;
    heroHeading: string;
    heroParagraph: string;
    paymentRoute: string;
    trackViewedEvent: () => void;
    onSave: (fields: Partial<F>) => Promise<V[]>;
    onClaim: () => Promise<
        MutationFetchResult<claimVideoBrief | claimPhotographyBrief, Record<string, any>>
    >;
    briefType: 'video' | 'photo';
    children: (form: ObservableForm<F>) => React.ReactNode;
}

interface State {
    packageError: string;
}

export class SmartShootBrief<
    F extends VideoBriefFields | PhotographyBriefFields,
    B extends getVideoBrief_structuredBrief | getPhotographyBrief_structuredBrief,
    V extends VideoValidationError | PhotographyValidationError
> extends React.Component<Props<B, F, V>, State> {
    form: ObservableForm<F>;

    constructor(props: Props<B, F, V>, state: State) {
        super(props, state);

        this.form = new ObservableForm<F>(this.props.brief.fields as F, {
            onSave: this.save,
            autosaveDelay: 1000,
        });

        this.handleFormSubmission = this.handleFormSubmission.bind(this);

        this.state = { packageError: '' };
    }

    save = async (changes: Partial<F>): Promise<FormErrors> => {
        const errors = await this.props.onSave(changes);
        return this.handleFormErrors(errors);
    };

    UNSAFE_componentWillReceiveProps(props: Props<B, F, V>) {
        this.form.setData(props.brief.fields as F);
    }

    UNSAFE_componentWillMount() {
        // Remove location search if set
        if (history.location.search && history.location.search !== '') {
            history.replace({
                pathname: history.location.pathname,
                search: '',
                state: {},
            });
        }
    }

    componentDidMount() {
        document.title = this.props.documentTitle;

        this.props.trackViewedEvent();

        if (this.isUserLoggedIn()) {
            handlePromise(this.claimIfUnclaimed(), 'claimIfUnclaimed on SmartShootBrief mount');
        }
    }

    footerActions() {
        return (
            <ButtonGroup variations={['responsive']} style={{ color: '#ff7e65' }}>
                <ButtonGroupItem>
                    <Button
                        variations={['primary', 'jumbo', 'responsive']}
                        onClick={this.handleFormSubmission}
                    >
                        Continue
                    </Button>
                </ButtonGroupItem>
            </ButtonGroup>
        );
    }

    render() {
        return (
            <SmartShootUSOnly>
                <BriefHero heading={this.props.heroHeading} paragraph={this.props.heroParagraph} />
                <section className="section">
                    {this.props.children(this.form)}
                    <PackageForm
                        step={4}
                        form={this.form}
                        errorMessage={this.state.packageError}
                        briefType={this.props.briefType}
                    />
                    <BriefFooter>{this.footerActions()}</BriefFooter>
                </section>
            </SmartShootUSOnly>
        );
    }

    private isUserLoggedIn(): boolean {
        const { user } = this.props.viewer;
        return !!user;
    }

    private async handleFormSubmission() {
        await this.form.save();

        if (this.form.hasErrors()) {
            this.updateStateWithFormErrors();
            this.scrollToFirstError();
            return;
        }

        let loggedIn = this.isUserLoggedIn();

        if (!loggedIn) {
            loggedIn = await this.showAuthModal();
        }

        if (loggedIn) {
            await this.claimIfUnclaimed();
            this.redirectToPaymentPage();
        }
    }

    private async showAuthModal(): Promise<boolean> {
        trackViewedBriefEvent({ id: this.props.brief.id, page: 'Login' });
        return await this.props.login({
            signupHeading: __("You're almost there..."),
            signupMessage: <p>{__('Sign up to kick off your project.')}</p>,
        });
    }

    private redirectToPaymentPage() {
        // Full page reload to refresh global nav.
        window.location.href = this.props.paymentRoute;
    }

    private async claimIfUnclaimed() {
        if (!this.props.brief.userId) {
            await this.props.onClaim();
        }
    }

    private handleFormErrors(errors: V[]): FormErrors {
        const validationErrors: FormErrors = {};

        errors.forEach(({ field, reason }) => {
            validationErrors[field] = (() => {
                switch (reason) {
                    case ErrorReason.TOO_LONG:
                        return 'This value is too long. Please try again';
                    case ErrorReason.TOO_SHORT:
                        switch (field) {
                            case 'phone':
                                return 'This field requires 7+ digits.';
                            default:
                                return 'This value is too short. Please try again.';
                        }
                    case ErrorReason.BLANK:
                        switch (field) {
                            case 'title':
                                return __("Don't forget to name your project.");
                            case 'category':
                                return __("Don't forget to choose a category.");
                            case 'description':
                                return __("Don't forget to describe your project.");
                            case 'package':
                                return 'Please select a package to continue:';
                            case 'datetime':
                                return 'These fields are required.';
                            case 'address':
                                return 'A full address is required.';
                            default:
                                return __('This field is required.');
                        }
                    case ErrorReason.INVALID:
                        return __('This doesn’t look quite right. Please try again.');
                    default:
                        Bugsnag.notify({
                            name: 'MissingValidationError',
                            message: `Missing validation error for "${reason}:${field}"`,
                        });
                        return '';
                }
            })();
        });

        return validationErrors;
    }

    private updateStateWithFormErrors() {
        this.setState({ packageError: this.form.getError('package') });
    }

    private scrollToFirstError = () => {
        const firstErrorName = this.form.firstError();
        if (!firstErrorName) {
            return;
        }

        let element = document.querySelector(`[name="${firstErrorName}"]`);
        if (!element) {
            element = document.querySelector(`[data-name="${firstErrorName}"]`);
        }

        if (!element) {
            return;
        }

        scrollToElement(element, 30, 300);
    };
}
